import type { E164 } from './types';

export type PhoneKind = 'mobile' | 'landline';
export type PhoneResult =
  | { ok: true; e164: E164; national: string; kind: PhoneKind }
  | { ok: false; reason: 'empty' | 'invalid_chars' | 'too_short' | 'too_long' | 'bad_prefix' };

/**
 * Normalises Israeli phone numbers to E.164 (+972...). Accepts 050-123-4567, 0501234567,
 * +972501234567, 972501234567, 00972501234567, "+972 (0)50 123 4567".
 * Mobile: 5X + 7 digits. Landline/VoIP: 2,3,4,8,9 or 7X + 7 digits.
 */
export function normalizeIsraeliPhone(input: string): PhoneResult {
  const raw = input.trim();
  if (raw === '') return { ok: false, reason: 'empty' };
  if (!/^[+\d\s\-().]+$/.test(raw)) return { ok: false, reason: 'invalid_chars' };
  let digits = raw.replace(/\D/g, '');
  const international = raw.startsWith('+') || digits.startsWith('00');
  if (international && !/^(\+|00)\s*972/.test(raw.replace(/\s/g, ''))) return { ok: false, reason: 'bad_prefix' };
  if (digits.startsWith('00972')) digits = digits.slice(5);
  else if (digits.startsWith('972')) digits = digits.slice(3);
  // "+972 (0)50..." keeps a stray trunk zero
  if (digits.startsWith('0')) digits = digits.slice(1);
  if (digits.length < 8) return { ok: false, reason: 'too_short' };
  if (digits.length > 9) return { ok: false, reason: 'too_long' };
  const mobile = /^5\d{8}$/.test(digits);
  const landline = /^[23489]\d{7}$/.test(digits) || /^7[2-7]\d{7}$/.test(digits);
  if (!mobile && !landline) return { ok: false, reason: 'bad_prefix' };
  return { ok: true, e164: `+972${digits}`, national: `0${digits}`, kind: mobile ? 'mobile' : 'landline' };
}

/** 050-123-4567 style display of an E.164 Israeli number (falls back to the input). */
export function formatPhone(e164: E164): string {
  const r = normalizeIsraeliPhone(e164);
  if (!r.ok) return e164;
  const n = r.national;
  return n.length === 10 ? `${n.slice(0, 3)}-${n.slice(3, 6)}-${n.slice(6)}` : `${n.slice(0, 2)}-${n.slice(2)}`;
}

/** wa.me link for a manual WhatsApp message. */
export function waMeLink(e164: E164, text?: string): string {
  const num = e164.replace(/\D/g, '');
  return text ? `https://wa.me/${num}?text=${encodeURIComponent(text)}` : `https://wa.me/${num}`;
}
